import { env } from 'next-runtime-env';

import { MediaTypes } from '~constants/media';
// TODO: принять решение: или исключить типы из правил FSD или рефакторить
import type {
	TMessage,
	TMessageMetaRegeneration,
	TSupportedMessageTypes,
	// eslint-disable-next-line boundaries/element-types
} from '~entities/messages';
import type { SendMessageApiRequest } from '~services/api/dialogs/types';
import { getRandomInt } from '~utils/numbers';
import { camelizeKeys } from '~utils/object';

export const buildMessageFromString = (
	message: string,
	id: string,
	payload?: {
		reference?: string;
		meta: SendMessageApiRequest['meta'];
	}
): SendMessageApiRequest => {
	const tag = `${id}_${Date.now() + getRandomInt()}`;
	const text = message.replace(/^\s+|\s+$|\s+(?=\s)/g, '');

	const msg = {
		tag,
		meta: payload?.meta || null,
		instant: 1,
		text,
		reference: payload?.reference,
	};

	if (!payload?.meta?.gift) {
		delete msg['reference'];
	}

	return msg;
};

export const transformMessageBodyToMessage = (
	message: SendMessageApiRequest,
	sender: string,
	recipient: string,
	prevId: number
): TMessage => ({
	id: prevId + 1,
	status: 0,
	read: false,
	...message,
	sender,
	recipient,
	type: 1,
	timestamp: Date.now(),
	meta: {
		...message.meta,
		tariffication: {
			paid: true,
			final: true,
		},
		auto: false,
	},
});

export const addEncodedTextFieldToMessage = (message: TMessage): TMessage => {
	if (message.meta?.tariffication?.paid) {
		return message;
	}
	return {
		...message,
		timestamp: message?.timestamp ?? Date.now(),
	};
};
export const normalizeFormat = (message: TMessage): TMessage => {
	// @ts-ignore
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const regObg = message.meta?.['Regeneration'];

	if (regObg) {
		return {
			...message,
			meta: message.meta
				? {
						...message.meta,
						regeneration: camelizeKeys(regObg) as TMessageMetaRegeneration,
					}
				: message.meta,
		};
	}
	return message;
};
export const adaptSextingMessage = (message: TMessage): TMessage => {
	return {
		...message,
		meta: message.meta
			? {
					...message.meta,
					isSexting: message.meta?.ranking?.labels?.includes?.('sexting'),
					hasWarning:
						message.meta?.ranking?.labels?.includes?.('blockedSexualContext') ||
						false,
				}
			: message.meta,
	};
};

export const adaptAudioMessage = (message: TMessage): TMessage => {
	const isAudio =
		(message.meta?.reference || '').includes('mp3') ||
		(message.meta?.reference || '').includes('audio');
	return {
		...message,
		meta: message.meta
			? {
					...message.meta,
					hasAudio: isAudio || message.meta.hasAudio,
				}
			: null,
	};
};

export const adaptSuggestionMessage = (
	message: TMessage,
	{
		continueMessage,
		photoMessage,
	}: { photoMessage: string; continueMessage: string }
): TMessage => {
	if (message.meta?.suggestionType === 'continue') {
		return { ...message, text: continueMessage };
	}
	if (message.meta?.suggestionType === 'photo') {
		return { ...message, text: photoMessage };
	}

	return message;
};

export const adaptVideoMessage = (message: TMessage): TMessage => {
	const reference = message.meta?.reference ?? '';
	const hasReference = reference.includes('video-ai:');
	const hasPreview = message.meta?.preview ?? ''.includes('photo-ai:');
	return {
		...message,
		meta: message.meta
			? {
					...message.meta,
					MediaId: message.meta.MediaId || message.meta.mediaId,
					reference: hasReference
						? transformVideoUrl({
								url: message.meta?.reference as string,
								botId: message.sender,
								userId: message.recipient,
								messageId: String(message.id),
							})
						: message.meta?.reference,
					preview: hasPreview
						? transformImageUrl({
								url: message.meta?.preview as string,
								botId: message.sender,
								userId: message.recipient,
								messageId: String(message.id),
							})
						: message.meta?.preview,
				}
			: null,
	};
};

export const addGifPath = (message: TMessage): TMessage => {
	return {
		...message,
		meta: message.meta
			? {
					...message.meta,
					giftPreview: message.meta?.gift ? message.meta?.reference : undefined,
					preview: message.meta?.preview || message.meta?.reference,
				}
			: null,
	};
};

export const formatIMGPaths = (message: TMessage): TMessage => {
	const isGift = !!message?.meta?.giftPreview;
	const reference = message.meta?.reference;
	const hasReference = (reference || '').includes('photo-ai');
	const hasPreview = (message.meta?.preview || '').includes('photo-ai'); // detects only blurred previews

	if (isGift || !hasReference) {
		return message;
	}
	return {
		...message,
		meta: message.meta
			? {
					...message.meta,
					reference: hasReference
						? transformImageUrl({
								url: message.meta?.reference as string,
								botId: message.sender,
								userId: message.recipient,
								messageId: String(message.id),
							})
						: message.meta?.reference,
					preview: transformImageUrl({
						url: hasPreview
							? (message.meta?.preview as string)
							: (message.meta?.reference?.replace('file', 'preview') as string),
						botId: message.sender,
						userId: message.recipient,
						messageId: String(message.id),
					}),
					mediaLinks: message.meta?.mediaLinks?.map((mediaLink) => {
						if (mediaLink.type === MediaTypes.cf) {
							return {
								...mediaLink,
								previewLinks: mediaLink.previewLinks?.map((previewLink) => {
									return {
										...previewLink,
										reference: transformImageUrl({
											url: previewLink.reference,
											botId: message.sender,
											userId: message.recipient,
											messageId: String(message.id),
										}),
									};
								}),
								sizeLinks: mediaLink.sizeLinks?.map((sizeLink) => {
									return {
										...sizeLink,
										reference: transformImageUrl({
											url: sizeLink.reference,
											botId: message.sender,
											userId: message.recipient,
											messageId: String(message.id),
										}),
									};
								}),
							};
						}

						return mediaLink;
					}),
				}
			: null,
	} as TMessage;
};

export const transformImageUrl = ({
	url,
	userId,
	messageId,
	botId,
}: {
	url: string;
	userId: string;
	messageId: string;
	botId: string;
}) =>
	url
		.replace('photo-ai:', env('NEXT_PUBLIC_PUBLIC_API') || '')
		.replace('{userId}', userId)
		.replace('{messageId}', String(messageId))
		.replace('{botId}', botId);

export const transformVideoUrl = ({
	url,
	userId,
	messageId,
	botId,
}: {
	url: string;
	userId: string;
	messageId: string;
	botId: string;
}) =>
	url
		.replace('long-video-ai:', '/api/video?url=')
		.replace('short-video-ai:', '/api/video?url=')
		.replace('{userId}', userId)
		.replace('{messageId}', String(messageId))
		.replace('{botId}', botId);

export const getMessageType = (
	meta: TMessage['meta']
): TSupportedMessageTypes => {
	const hasAudio = meta?.hasAudio === true;
	const loading = meta?.messageType === 'generationInitiated';
	const hasVideo =
		meta?.mediaType === 'video' ||
		meta?.mediaType === 'long_video' ||
		meta?.mediaType === 'short_video' ||
		meta?.messageType === 'public-video' ||
		meta?.messageType === 'private-video' ||
		// fallback
		typeof meta?.videoDuration === 'number';
	const isSextingPack = meta?.type === 'pack' || meta?.mediaType === 'pack';
	const isImage =
		meta?.mediaType === 'photo' ||
		meta?.messageType === 'public-image' ||
		meta?.messageType === 'private-image' ||
		meta?.messageType === 'public-photo' ||
		meta?.messageType === 'private-photo' ||
		meta?.reference;
	if (hasVideo) {
		return loading ? 'video-loading' : 'video';
	}
	if (isSextingPack) {
		return 'pack';
	}
	if (hasAudio) {
		return 'audio';
	}

	if (meta?.giftPreview) {
		return 'gift';
	}

	if (isImage) {
		return loading ? 'image-loading' : 'image';
	}

	return 'text';
};
